import Modal from "@/components/Modal";
import SkeletonRow from "@/components/SkeletonRow";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { listSemester } from "@/constants";
import useDataStep from "@/hooks/useDataStep";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { getListNilaiRaporPenerimaan } from "@/services/getData";
import { DataNilaiRapor } from "@/types";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Eye,
  Info,
  Pencil,
} from "lucide-react"; // Gunakan Lucide Icons
import { useEffect, useState } from "react";
import Form from "./partial/Form";
import PdfViewer from "@/components/PdfViewer";

export default function NilaiRapor({
  idDataPendaftaran,
}: {
  idDataPendaftaran: string;
}) {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });
  const { timePassedActive } = useTimePeriode();
  const { isSudahFinalisasi, isValidasiPendaftaran } = useDataStep();

  const { data: listNilaiRapor, isPending: isPendingListNilaiRapor } = useQuery(
    {
      queryKey: ["listNilaiRapor", sessionPeriode?.id_sys_periode],
      queryFn: () =>
        getListNilaiRaporPenerimaan(sessionPeriode?.id_sys_periode || ""),
    },
  );

  const [listNilaiRaporFormated, setListNilaiRaporFormated] = useState<
    DataNilaiRapor[]
  >((listSemester as DataNilaiRapor[]) || []);
  const [selectedRow, setSelectedRow] = useState<DataNilaiRapor | null>(null);
  const [showInput, setShowInput] = useState(false);
  const [showFile, setShowFile] = useState(false);

  useEffect(() => {
    if (!listNilaiRapor || listNilaiRapor?.length === 0) return;

    const merged = listSemester.map((semesterItem) => {
      const nilaiRapor = listNilaiRapor.find(
        (dataNilaiRapor: DataNilaiRapor) =>
          dataNilaiRapor.semester === semesterItem.semester,
      );
      return { ...semesterItem, ...nilaiRapor };
    });

    setListNilaiRaporFormated(merged);
  }, [listNilaiRapor]);

  const isDisableInput =
    isSudahFinalisasi ||
    isValidasiPendaftaran ||
    timePassedActive?.waktu_selesai_daftar_valid_selesai;

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* HEADER */}
      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-4">
        <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            Nilai Rapor
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Input nilai rata-rata pengetahuan per semester.
          </p>
        </div>
      </div>

      {/* ALERT INFO */}
      <Alert className="bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
        <Info className="h-4 w-4" />
        <AlertTitle className="font-bold">Ketentuan Pengisian</AlertTitle>
        <AlertDescription className="text-xs space-y-1 mt-1">
          <p>
            • Masukkan Rata-Rata <strong>Nilai Pengetahuan tiap semester (1 s.d 5)</strong>.
          </p>
          <p>
            • Unggah hasil scan <strong>rapor asli</strong> yang terbaca jelas (PDF). Pastikan rapor yang diunggah adalah rapor dari sekolah, <strong>bukan rekap nilai buatan sendiri (seperti Excel).</strong>
          </p>
          <p>
            • Rapor asli wajib memuat tanda tangan Kepala Sekolah dan dibubuhi cap basah resmi sekolah.
          </p>
        </AlertDescription>
      </Alert>

      {/* CONTENT: SEMESTER LIST */}
      {isPendingListNilaiRapor ? (
        <div className="grid md:grid-cols-2 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonRow key={i} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {listNilaiRaporFormated.map((data, index) => {
            const isFilled =
              data.rata_rata_nilai !== undefined &&
              data.rata_rata_nilai !== null;
            const hasFile = !!data.file_rapor_semester;

            return (
              <div
                key={index}
                className={`relative flex flex-col p-5 rounded-xl border transition-all ${isFilled && hasFile
                  ? "bg-white border-slate-200 dark:bg-slate-900 dark:border-slate-800 hover:border-blue-400"
                  : "bg-slate-50 border-dashed border-slate-300 dark:bg-slate-950 dark:border-slate-700"
                  }`}
              >
                {/* Header Card */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className="bg-white dark:bg-slate-950 font-bold"
                      >
                        {data.alias}
                      </Badge>
                      {isFilled && hasFile && (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {data.kelas} • Semester {data.semester}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-[10px] uppercase text-slate-400 font-bold tracking-wider mb-0.5">
                      Rata-Rata
                    </p>
                    <span
                      className={`text-2xl font-bold ${isFilled
                        ? "text-blue-600 dark:text-blue-400"
                        : "text-slate-300 dark:text-slate-700"
                        }`}
                    >
                      {data.rata_rata_nilai ?? "0"}
                    </span>
                  </div>
                </div>

                {/* Info Kurikulum */}
                <div className="mb-4">
                  {data.kurikulum ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300">
                      <BookOpen className="w-3 h-3" /> {data.kurikulum}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400 italic">
                      Kurikulum belum dipilih
                    </span>
                  )}
                </div>

                {/* Actions Footer */}
                <div className="mt-auto pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
                  {/* File Status / Action */}
                  {hasFile ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 px-2"
                      onClick={() => {
                        setShowFile(true);
                        setSelectedRow(data);
                      }}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1.5" /> Lihat Rapor
                    </Button>
                  ) : (
                    <div className="flex items-center text-xs text-amber-600 dark:text-amber-500 px-2">
                      <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Belum
                      Upload
                    </div>
                  )}

                  {/* Input Button */}
                  {!isDisableInput && (
                    <Button
                      size="sm"
                      className={`text-xs h-8 ${isFilled
                        ? "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
                        : ""
                        }`}
                      onClick={() => {
                        setShowInput(true);
                        setSelectedRow(data);
                      }}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1.5" />
                      {isFilled ? "Edit" : "Input"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* --- MODAL INPUT FORM --- */}
      <Modal
        show={showInput}
        onClose={() => setShowInput(false)}
        className="lg:w-[45vw] w-[95vw]"
      >
        <Form
          closeModal={() => setShowInput(false)}
          idDataPendaftaran={idDataPendaftaran}
          selectedRow={selectedRow}
        />
      </Modal>

      {/* --- MODAL PREVIEW FILE --- */}
      <Modal
        show={showFile}
        onClose={() => setShowFile(false)}
        className="lg:h-[95vh] lg:w-[60vw] w-[95vw]"
      >
        <PdfViewer url={`${selectedRow?.file_rapor_semester}`} />
      </Modal>
    </div>
  );
}
